import { useEffect, useState } from "react";
import {
  AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import { SignIn, Users as UsersIcon, Pulse } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { useAdminNav } from "@/pages/adminNav";

const PERIODS = [
  { k: "7d", l: "7 days" }, { k: "30d", l: "30 days" }, { k: "90d", l: "90 days" }, { k: "all", l: "All time" },
];

export default function AdminActivity() {
  const { openReceipts } = useAdminNav();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/admin/activity?period=${period}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [period]);

  if (data === false) return <div className="text-muted-foreground">Could not load activity.</div>;

  return (
    <div className="space-y-6" data-testid="admin-activity">
      <div className="flex flex-wrap items-center gap-2">
        {PERIODS.map((p) => (
          <button key={p.k} data-testid={`activity-period-${p.k}`} onClick={() => setPeriod(p.k)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${period === p.k ? "bg-riq-primary text-white" : "border border-riq-border text-neutral-600 hover:border-riq-primary"}`}>
            {p.l}
          </button>
        ))}
      </div>

      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={SignIn} label="Logins today" value={data.summary.today.logins} sub={`${data.summary.today.active} active users`} />
            <Kpi icon={SignIn} label="Logins (7d)" value={data.summary.last_7d.logins} sub={`${data.summary.last_7d.active} active users`} />
            <Kpi icon={SignIn} label="Logins this month" value={data.summary.this_month.logins} sub={`${data.summary.this_month.active} active users`} />
            <Kpi icon={UsersIcon} label="Active (30d)" value={data.summary.last_30d.active} sub={`${data.summary.last_30d.logins} logins`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TrendCard title="Logins over time" data={data.daily} dataKey="logins" color="#6E3FFC" icon={SignIn} />
            <TrendCard title="Active users per day" data={data.daily} dataKey="active" color="#42C97C" icon={Pulse} />
          </div>

          <Card data-testid="activity-top-users">
            <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
              <Pulse size={18} weight="duotone" /> Most active users
            </h3>
            {data.top_active.length ? (
              <div className="divide-y divide-riq-border -mx-1">
                {data.top_active.map((u, i) => (
                  <div key={i} className="flex items-center gap-3 px-1 py-2.5 text-sm">
                    <span className="w-6 h-6 rounded-full bg-riq-primary/10 text-riq-primary text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <button
                        data-testid={`activity-user-link-${i}`}
                        onClick={() => openReceipts({ user_id: u.user_id, user_email: u.email })}
                        className="font-medium truncate text-riq-primary hover:underline text-left block max-w-full"
                        title="Show this user's receipts"
                      >
                        {u.name || u.email}
                      </button>
                      <div className="text-xs text-muted-foreground truncate">{u.area || u.email}</div>
                    </div>
                    <span className="text-muted-foreground text-xs">{u.last ? String(u.last).slice(0, 10) : "—"}</span>
                    <span className="font-semibold w-16 text-right">{u.logins} <span className="font-normal text-muted-foreground text-xs">logins</span></span>
                  </div>
                ))}
              </div>
            ) : <Empty />}
          </Card>
        </>
      )}
    </div>
  );
}

// (helper removed — using Pulse icon directly)

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-4">
      <Icon size={18} weight="duotone" className="text-riq-primary mb-2" />
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-head text-2xl font-bold text-riq-primary mt-0.5 truncate">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

function TrendCard({ title, data, dataKey, color, icon: Icon }) {
  const id = dataKey;
  return (
    <Card>
      <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2 mb-4">
        <Icon size={18} weight="duotone" /> {title}
      </h3>
      {data.length ? (
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={data} margin={{ left: -20, right: 4, top: 4, bottom: 0 }}>
            <defs>
              <linearGradient id={`ac-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d?.slice(5)} minTickGap={24} />
            <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
            <Tooltip formatter={(v) => v} />
            <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} fill={`url(#ac-${id})`} />
          </AreaChart>
        </ResponsiveContainer>
      ) : <Empty />}
    </Card>
  );
}

function Empty() {
  return <div className="text-sm text-muted-foreground py-6 text-center">No data for this period.</div>;
}
